// src/services/indoorMappingService.js
import { axiosInstance } from './apiClient';

/**
 * Service for handling indoor mapping and wayfinding API interactions
 * Uses the shared axiosInstance from apiClient for proper authentication via IndexedDB
 *
 * Backend API Routes (dynamically loaded from routes/dynamic/):
 * - Floor Plans: /api/floorPlans/*
 * - POIs: /api/pois/*
 * - Entrances: /api/entrances/*
 * - Wayfinding (nodes/edges): /api/wayfinding/*
 * - Navigation (routes): /api/navigation/*
 */
class IndoorMappingService {
  /**
   * Get axios instance with auth (uses shared apiClient instance)
   * @private
   */
  static #getAxiosInstance() {
    return axiosInstance;
  }

  // ==========================================
  // Floor Plan Methods
  // ==========================================

  /**
   * Get all floor plans (across all locations)
   * @returns {Promise<Array>} Array of floor plan objects
   */
  static async getAllFloorPlans() {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get('/floorPlans');
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch all floor plans');
    }
  }

  /**
   * Get all floor plans for a location
   * @param {string} locationId - Location identifier
   * @returns {Promise<Array>} Array of floor plan objects
   */
  static async getFloorPlans(locationId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/floorPlans/location/${locationId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch floor plans');
    }
  }

  /**
   * Get a specific floor plan by ID
   * @param {string} floorPlanId - Floor plan identifier
   * @returns {Promise<Object>} Floor plan object
   */
  static async getFloorPlan(floorPlanId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/floorPlans/${floorPlanId}`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, `Failed to fetch floor plan ${floorPlanId}`);
    }
  }

  /**
   * Upload floor plan PDF
   * @param {string} locationId - Location identifier
   * @param {File} file - PDF file to upload
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Upload result with generated floors
   */
  static async uploadFloorPlanPdf(locationId, file, onProgress = null) {
    try {
      const instance = this.#getAxiosInstance();
      const formData = new FormData();
      // Backend expects 'location_id', not 'locationId'
      formData.append('location_id', locationId);
      // Append file last to ensure proper multipart boundary handling
      formData.append('pdf', file, file.name);

      console.log('[IndoorMappingService] Uploading to /floorPlans/upload', {
        locationId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const response = await instance.post(
        '/floorPlans/upload',
        formData,
        {
          headers: {
            // Let browser set the Content-Type with boundary automatically
            // by explicitly setting it to undefined, axios won't override
            'Content-Type': undefined
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted);
            }
          },
        }
      );

      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to upload floor plan PDF');
    }
  }

  /**
   * Create a floor plan manually
   * @param {Object} floorPlanData - Floor plan data
   * @returns {Promise<Object>} Created floor plan
   */
  static async createFloorPlan(floorPlanData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post('/floorPlans', floorPlanData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to create floor plan');
    }
  }

  /**
   * Update a floor plan
   * @param {string} floorPlanId - Floor plan identifier
   * @param {Object} floorPlanData - Updated floor plan data
   * @returns {Promise<Object>} Updated floor plan
   */
  static async updateFloorPlan(floorPlanId, floorPlanData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.put(`/floorPlans/${floorPlanId}`, floorPlanData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to update floor plan');
    }
  }

  /**
   * Delete a floor plan
   * @param {string} floorPlanId - Floor plan identifier
   * @returns {Promise<void>}
   */
  static async deleteFloorPlan(floorPlanId) {
    try {
      const instance = this.#getAxiosInstance();
      await instance.delete(`/floorPlans/${floorPlanId}`);
    } catch (error) {
      this.#handleError(error, 'Failed to delete floor plan');
    }
  }

  /**
   * Save geo-reference (bounds) for a floor plan
   * @param {string} floorPlanId - Floor plan identifier
   * @param {Object} bounds - Geographic bounds { north, south, east, west }
   * @returns {Promise<Object>} Updated floor plan
   */
  static async saveGeoReference(floorPlanId, bounds) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.patch(`/floorPlans/${floorPlanId}/geo-reference`, {
        bounds,
      });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to save geo-reference');
    }
  }

  /**
   * Set a floor plan as default for its location
   * @param {string} floorPlanId - Floor plan identifier
   * @returns {Promise<Object>} Updated floor plan
   */
  static async setDefaultFloorPlan(floorPlanId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.patch(`/floorPlans/${floorPlanId}/set-default`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to set default floor plan');
    }
  }

  // ==========================================
  // Wayfinding Graph Methods
  // ==========================================

  /**
   * Get complete wayfinding graph (nodes + edges) for a floor plan
   * @param {string} floorPlanId - Floor plan identifier
   * @returns {Promise<Object>} Object with nodes and edges arrays
   */
  static async getWayfindingGraph(floorPlanId) {
    try {
      const instance = this.#getAxiosInstance();

      // Fetch nodes for this floor plan
      let nodes = [];
      try {
        const nodesResponse = await instance.get(`/wayfinding/nodes/floor-plan/${floorPlanId}`);
        nodes = nodesResponse.data?.data || nodesResponse.data || [];
      } catch (err) {
        console.warn('[IndoorMappingService] No wayfinding nodes found:', err.message);
        nodes = [];
      }

      // Fetch edges - get all edges and filter to those connecting nodes in this floor plan
      let edges = [];
      if (nodes.length > 0) {
        try {
          const nodeIds = new Set(nodes.map(n => n.id));
          // Fetch all edges (with high limit) and filter client-side
          const edgesResponse = await instance.get('/wayfinding/edges', { params: { limit: 1000 } });
          const allEdges = edgesResponse.data?.data || edgesResponse.data || [];

          // Filter edges where both from_node_id and to_node_id are in our floor plan
          edges = allEdges.filter(e =>
            nodeIds.has(e.from_node_id) && nodeIds.has(e.to_node_id)
          );
          console.log(`[IndoorMappingService] Loaded ${edges.length} edges for floor plan ${floorPlanId}`);
        } catch (err) {
          console.warn('[IndoorMappingService] Could not load edges:', err.message);
          edges = [];
        }
      }

      return { nodes, edges };
    } catch (error) {
      console.error('[IndoorMappingService] getWayfindingGraph error:', error);
      // Return empty graph instead of throwing
      return { nodes: [], edges: [] };
    }
  }

  // ==========================================
  // Wayfinding Node Methods
  // ==========================================

  /**
   * Get wayfinding nodes for a floor plan
   * @param {string} floorPlanId - Floor plan identifier
   * @returns {Promise<Array>} Array of wayfinding nodes
   */
  static async getWayfindingNodes(floorPlanId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/wayfinding/nodes/floor-plan/${floorPlanId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch wayfinding nodes');
    }
  }

  /**
   * Get a single wayfinding node by ID
   * @param {string} nodeId - Node identifier
   * @returns {Promise<Object>} Wayfinding node
   */
  static async getWayfindingNode(nodeId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/wayfinding/nodes/${nodeId}`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to fetch wayfinding node');
    }
  }

  /**
   * Create a wayfinding node
   * @param {Object} nodeData - Node data { floor_plan_id, node_type, x_position, y_position, geo_lat, geo_lng }
   * @returns {Promise<Object>} Created node
   */
  static async createWayfindingNode(nodeData) {
    try {
      const instance = this.#getAxiosInstance();
      console.log('[IndoorMappingService] Creating wayfinding node:', nodeData);
      const response = await instance.post('/wayfinding/nodes', nodeData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to create wayfinding node');
    }
  }

  /**
   * Bulk create wayfinding nodes
   * @param {Array} nodesData - Array of node data objects
   * @returns {Promise<Array>} Created nodes
   */
  static async bulkCreateWayfindingNodes(nodesData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post('/wayfinding/nodes/bulk', { nodes: nodesData });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to bulk create wayfinding nodes');
    }
  }

  /**
   * Update a wayfinding node
   * @param {string} nodeId - Node identifier
   * @param {Object} nodeData - Updated node data
   * @returns {Promise<Object>} Updated node
   */
  static async updateWayfindingNode(nodeId, nodeData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.put(`/wayfinding/nodes/${nodeId}`, nodeData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to update wayfinding node');
    }
  }

  /**
   * Delete a wayfinding node
   * @param {string} nodeId - Node identifier
   * @returns {Promise<void>}
   */
  static async deleteWayfindingNode(nodeId) {
    try {
      const instance = this.#getAxiosInstance();
      await instance.delete(`/wayfinding/nodes/${nodeId}`);
    } catch (error) {
      this.#handleError(error, 'Failed to delete wayfinding node');
    }
  }

  // ==========================================
  // Wayfinding Edge Methods
  // ==========================================

  /**
   * Get wayfinding edges for a node
   * @param {string} nodeId - Node identifier
   * @returns {Promise<Array>} Array of edges connected to the node
   */
  static async getWayfindingEdges(nodeId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/wayfinding/edges/node/${nodeId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch wayfinding edges');
    }
  }

  /**
   * Create a wayfinding edge (connection between nodes)
   * @param {Object} edgeData - Edge data { from_node_id, to_node_id, edge_type, is_bidirectional }
   * @returns {Promise<Object>} Created edge
   */
  static async createWayfindingEdge(edgeData) {
    try {
      const instance = this.#getAxiosInstance();
      console.log('[IndoorMappingService] Creating wayfinding edge:', edgeData);
      const response = await instance.post('/wayfinding/edges', edgeData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to create wayfinding edge');
    }
  }

  /**
   * Bulk create wayfinding edges
   * @param {Array} edgesData - Array of edge data objects
   * @returns {Promise<Array>} Created edges
   */
  static async bulkCreateWayfindingEdges(edgesData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post('/wayfinding/edges/bulk', { edges: edgesData });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to bulk create wayfinding edges');
    }
  }

  /**
   * Update a wayfinding edge
   * @param {string} edgeId - Edge identifier
   * @param {Object} edgeData - Updated edge data
   * @returns {Promise<Object>} Updated edge
   */
  static async updateWayfindingEdge(edgeId, edgeData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.put(`/wayfinding/edges/${edgeId}`, edgeData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to update wayfinding edge');
    }
  }

  /**
   * Delete a wayfinding edge
   * @param {string} edgeId - Edge identifier
   * @returns {Promise<void>}
   */
  static async deleteWayfindingEdge(edgeId) {
    try {
      const instance = this.#getAxiosInstance();
      await instance.delete(`/wayfinding/edges/${edgeId}`);
    } catch (error) {
      this.#handleError(error, 'Failed to delete wayfinding edge');
    }
  }

  /**
   * Get multi-floor connections for a node (stairs/elevator edges with floor info)
   * @param {string} nodeId - Node identifier
   * @returns {Promise<Array>} Array of multi-floor connection objects with floor info
   */
  static async getNodeMultiFloorConnections(nodeId) {
    try {
      const instance = this.#getAxiosInstance();

      // Get all edges for this node
      const edges = await this.getWayfindingEdges(nodeId);

      // Filter to multi-floor edges (stairs/elevator types)
      const multiFloorEdges = edges.filter(e => {
        const edgeType = e.edge_type || '';
        return edgeType.includes('stairs') || edgeType.includes('elevator');
      });

      // For each edge, get the target node and its floor plan info
      const connectionsWithFloorInfo = await Promise.all(
        multiFloorEdges.map(async (edge) => {
          const isOutgoing = edge.from_node_id === nodeId;
          const targetNodeId = isOutgoing ? edge.to_node_id : edge.from_node_id;

          try {
            // Get the target node to find its floor plan
            const targetNode = await this.getWayfindingNode(targetNodeId);
            let targetFloorName = null;

            if (targetNode?.floor_plan_id) {
              try {
                const floorPlan = await this.getFloorPlan(targetNode.floor_plan_id);
                targetFloorName = floorPlan?.floor_name || `Floor ${floorPlan?.floor_number}`;
              } catch (err) {
                console.warn('Could not fetch floor plan for node:', targetNodeId);
              }
            }

            return {
              ...edge,
              target_node_id: targetNodeId,
              target_floor_name: targetFloorName,
              target_node: targetNode,
            };
          } catch (err) {
            console.warn('Could not fetch target node:', targetNodeId);
            return {
              ...edge,
              target_node_id: targetNodeId,
              target_floor_name: null,
            };
          }
        })
      );

      return connectionsWithFloorInfo;
    } catch (error) {
      console.error('Failed to get multi-floor connections:', error);
      return [];
    }
  }

  // ==========================================
  // POI Methods (Points of Interest)
  // ==========================================

  /**
   * Get all POI types
   * @returns {Promise<Array>} Array of POI type objects
   */
  static async getPOITypes() {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get('/pois/types');
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch POI types');
    }
  }

  /**
   * Get POIs for a floor plan
   * @param {string} floorPlanId - Floor plan identifier
   * @returns {Promise<Array>} Array of POI objects
   */
  static async getPOIs(floorPlanId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/pois/floor-plan/${floorPlanId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch POIs');
    }
  }

  /**
   * Create a POI
   * @param {string} floorPlanId - Floor plan identifier
   * @param {Object} poiData - POI data { name, poi_type_id, x_position, y_position, description }
   * @returns {Promise<Object>} Created POI
   */
  static async createPOI(floorPlanId, poiData) {
    try {
      const instance = this.#getAxiosInstance();

      // Debug: log what we received
      console.log('[IndoorMappingService] createPOI called with:', { floorPlanId, poiData });

      // Get geo coordinates - check all possible field names
      // Use explicit checks since 0 is a valid coordinate
      const geoLat = poiData.geo_lat !== undefined ? poiData.geo_lat
        : poiData.lat !== undefined ? poiData.lat
        : poiData.y_position;

      const geoLng = poiData.geo_lng !== undefined ? poiData.geo_lng
        : poiData.lng !== undefined ? poiData.lng
        : poiData.x_position;

      // Transform local format to API format
      // Backend requires EITHER (x_position, y_position) OR (geo_lat, geo_lng)
      // We send both to ensure compatibility - use geo coords as x/y if not provided
      const apiData = {
        floor_plan_id: floorPlanId,
        name: poiData.name,
        poi_type_id: poiData.poi_type_id,
        // Geographic coordinates
        geo_lat: geoLat,
        geo_lng: geoLng,
        // Pixel/map positions - use geo coords as fallback since the POI editor uses geo coordinates
        x_position: poiData.x_position !== undefined ? poiData.x_position : geoLng,
        y_position: poiData.y_position !== undefined ? poiData.y_position : geoLat,
        description: poiData.description || null,
      };

      console.log('[IndoorMappingService] Creating POI with API data:', apiData);
      const response = await instance.post('/pois', apiData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to create POI');
    }
  }

  /**
   * Update a POI
   * @param {string} poiId - POI identifier
   * @param {Object} poiData - Updated POI data
   * @returns {Promise<Object>} Updated POI
   */
  static async updatePOI(poiId, poiData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.put(`/pois/${poiId}`, poiData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to update POI');
    }
  }

  /**
   * Delete a POI
   * @param {string} poiId - POI identifier
   * @returns {Promise<void>}
   */
  static async deletePOI(poiId) {
    try {
      const instance = this.#getAxiosInstance();
      await instance.delete(`/pois/${poiId}`);
    } catch (error) {
      this.#handleError(error, 'Failed to delete POI');
    }
  }

  // ==========================================
  // Route Calculation Methods
  // ==========================================

  /**
   * Get entrances for a location
   * @param {string} locationId - Location identifier
   * @returns {Promise<Array>} Array of entrance objects
   */
  static async getEntrances(locationId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/entrances/location/${locationId}`);
      return response.data?.data || response.data || [];
    } catch (error) {
      this.#handleError(error, 'Failed to fetch entrances');
    }
  }

  /**
   * Create a building entrance
   * @param {Object} entranceData - Entrance data
   * @param {string} entranceData.location_id - Location identifier
   * @param {string} entranceData.floor_plan_id - Floor plan identifier
   * @param {string} entranceData.name - Entrance name
   * @param {string} entranceData.entrance_type - Type: 'main', 'side', 'emergency', 'accessible'
   * @param {number} entranceData.x_position - X coordinate (longitude)
   * @param {number} entranceData.y_position - Y coordinate (latitude)
   * @param {string} [entranceData.wayfinding_node_id] - Optional linked wayfinding node
   * @param {number} [entranceData.geo_lat] - Geographic latitude
   * @param {number} [entranceData.geo_lng] - Geographic longitude
   * @param {boolean} [entranceData.is_default] - Whether this is the default entrance
   * @param {boolean} [entranceData.is_accessible] - Whether this entrance is accessible
   * @returns {Promise<Object>} Created entrance
   */
  static async createEntrance(entranceData) {
    try {
      const instance = this.#getAxiosInstance();
      console.log('[IndoorMappingService] Creating building entrance:', entranceData);
      const response = await instance.post('/entrances', entranceData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to create building entrance');
    }
  }

  /**
   * Update a building entrance
   * @param {string} entranceId - Entrance identifier
   * @param {Object} entranceData - Updated entrance data
   * @returns {Promise<Object>} Updated entrance
   */
  static async updateEntrance(entranceId, entranceData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.put(`/entrances/${entranceId}`, entranceData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to update building entrance');
    }
  }

  /**
   * Delete a building entrance
   * @param {string} entranceId - Entrance identifier
   * @returns {Promise<void>}
   */
  static async deleteEntrance(entranceId) {
    try {
      const instance = this.#getAxiosInstance();
      await instance.delete(`/entrances/${entranceId}`);
    } catch (error) {
      this.#handleError(error, 'Failed to delete building entrance');
    }
  }

  /**
   * Calculate route from entrance to destination POI
   * Uses backend A* pathfinding to create NavigationRoute and NavigationSteps
   * @param {string} entranceId - Entrance identifier (required)
   * @param {string} destinationPoiId - Destination POI identifier
   * @param {Object} options - Route options
   * @param {boolean} options.accessible_only - Find wheelchair-accessible route only
   * @param {boolean} options.prefer_elevators - Prefer elevators over stairs
   * @param {boolean} options.use_cache - Check for cached route first (default: true)
   * @returns {Promise<Object>} Route object with steps
   */
  static async calculateRoute(entranceId, destinationPoiId, options = {}) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post('/navigation/calculate', {
        entrance_id: entranceId,
        destination_poi_id: destinationPoiId,
        accessible_only: options.accessible_only || false,
        prefer_elevators: options.prefer_elevators || false,
        use_cache: options.use_cache !== undefined ? options.use_cache : true,
      });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to calculate route');
    }
  }

  /**
   * Calculate route to custom coordinate (not a POI)
   * Uses backend A* pathfinding to create NavigationRoute and NavigationSteps
   * @param {string} entranceId - Entrance identifier (required)
   * @param {string} floorPlanId - Floor plan identifier
   * @param {number} x - X coordinate on floor plan
   * @param {number} y - Y coordinate on floor plan
   * @param {Object} options - Route options
   * @returns {Promise<Object>} Route object with steps
   */
  static async calculateRouteToCustom(entranceId, floorPlanId, x, y, options = {}) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post('/navigation/calculate', {
        entrance_id: entranceId,
        destination_custom: {
          floorPlanId: floorPlanId,
          x: x,
          y: y,
        },
        accessible_only: options.accessible_only || false,
        prefer_elevators: options.prefer_elevators || false,
        use_cache: options.use_cache !== undefined ? options.use_cache : true,
      });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to calculate route');
    }
  }

  /**
   * Calculate route from entrance to a specific wayfinding node
   * Uses backend A* pathfinding with node-to-node navigation
   * @param {string} entranceId - Entrance identifier (required)
   * @param {string} destinationNodeId - Destination wayfinding node ID
   * @param {Object} options - Route options
   * @returns {Promise<Object>} Route object with steps
   */
  static async calculateRouteToNode(entranceId, destinationNodeId, options = {}) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post('/navigation/calculate', {
        entrance_id: entranceId,
        destination_node_id: destinationNodeId,
        accessible_only: options.accessible_only || false,
        prefer_elevators: options.prefer_elevators || false,
        minimize_floor_changes: options.minimize_floor_changes || false, // Prefer routes with fewer floor transitions
        use_cache: options.use_cache !== undefined ? options.use_cache : true,
      });
      const routeData = response.data?.data || response.data;
      // Debug: Log the raw API response to verify destination is included
      console.log('[indoorMappingService] calculateRouteToNode response:', routeData);
      console.log('[indoorMappingService] destination from API:', routeData?.destination);
      return routeData;
    } catch (error) {
      this.#handleError(error, 'Failed to calculate route to node');
    }
  }

  /**
   * Get all navigation routes
   * @param {Object} params - Query parameters for filtering
   * @returns {Promise<Object>} Routes with pagination
   */
  static async getNavigationRoutes(params = {}) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get('/navigation/routes', { params });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to fetch navigation routes');
    }
  }

  /**
   * Get navigation statistics for a location
   * @param {string} locationId - Location identifier
   * @returns {Promise<Object>} Navigation statistics
   */
  static async getNavigationStatistics(locationId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/navigation/statistics/${locationId}`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to fetch navigation statistics');
    }
  }

  /**
   * Get a route by ID
   * @param {string} routeId - Route identifier
   * @returns {Promise<Object>} Route object
   */
  static async getRoute(routeId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/navigation/routes/${routeId}`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to fetch route');
    }
  }

  /**
   * Get a route by UUID (public access)
   * @param {string} uuid - Route UUID
   * @returns {Promise<Object>} Route object
   */
  static async getRouteByUuid(uuid) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/navigation/routes/uuid/${uuid}`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to fetch route');
    }
  }

  // ==========================================
  // Step Photo Methods
  // ==========================================

  /**
   * Upload step photo
   * @param {string} nodeId - Node identifier
   * @param {File} file - Photo file
   * @param {Object} metadata - Photo metadata { direction, caption }
   * @returns {Promise<Object>} Uploaded photo data
   */
  static async uploadStepPhoto(nodeId, file, metadata = {}) {
    try {
      const instance = this.#getAxiosInstance();
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('direction', metadata.direction || 'forward');
      formData.append('caption', metadata.caption || '');

      const response = await instance.post(
        `/wayfinding/nodes/${nodeId}/photos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to upload step photo');
    }
  }

  /**
   * Delete step photo
   * @param {string} photoId - Photo identifier
   * @returns {Promise<void>}
   */
  static async deleteStepPhoto(photoId) {
    try {
      const instance = this.#getAxiosInstance();
      await instance.delete(`/wayfinding/photos/${photoId}`);
    } catch (error) {
      this.#handleError(error, 'Failed to delete step photo');
    }
  }

  // ==========================================
  // Location Reset Methods
  // ==========================================

  /**
   * Reset all indoor mapping data for a location
   * Deletes: floor plans, POIs, wayfinding nodes/edges, entrances, step photos, navigation routes
   * @param {string} locationId - Location identifier
   * @returns {Promise<Object>} Result with deleted counts
   */
  static async resetLocationIndoorMapping(locationId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.delete(`/floorPlans/location/${locationId}/reset`);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to reset location indoor mapping data');
    }
  }

  // ==========================================
  // Node Calibration Methods
  // ==========================================

  /**
   * Get calibration points for a building
   * @param {string} buildingId - Building/location identifier
   * @returns {Promise<Object>} Calibration data with points
   */
  static async getCalibrationPoints(buildingId) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.get(`/wayfinding/calibration/${buildingId}`);
      return response.data?.data || response.data;
    } catch (error) {
      // Return empty if no calibration exists yet
      if (error.response?.status === 404) {
        return { points: [] };
      }
      this.#handleError(error, 'Failed to fetch calibration points');
    }
  }

  /**
   * Save calibration data for a building
   * @param {string} buildingId - Building/location identifier
   * @param {Object} calibrationData - Calibration data including transformation matrix and points
   * @returns {Promise<Object>} Saved calibration data
   */
  static async saveCalibrationData(buildingId, calibrationData) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.post(`/wayfinding/calibration/${buildingId}`, calibrationData);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to save calibration data');
    }
  }

  /**
   * Batch update node coordinates after calibration
   * @param {Array} updates - Array of { nodeId, floorPlanId, geo_lat, geo_lng }
   * @returns {Promise<Object>} Result with updated count
   */
  static async batchUpdateNodeCoordinates(updates) {
    try {
      const instance = this.#getAxiosInstance();
      const response = await instance.put('/wayfinding/nodes/batch-coordinates', { updates });
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to batch update node coordinates');
    }
  }

  /**
   * Update a single node's GPS coordinates
   * @param {string} nodeId - Node identifier
   * @param {number} geoLat - Latitude
   * @param {number} geoLng - Longitude
   * @param {number} [altitude] - Optional altitude
   * @returns {Promise<Object>} Updated node
   */
  static async updateNodeGeoCoordinates(nodeId, geoLat, geoLng, altitude = null) {
    try {
      const instance = this.#getAxiosInstance();
      const data = { geo_lat: geoLat, geo_lng: geoLng };
      if (altitude !== null) {
        data.altitude = altitude;
      }
      const response = await instance.patch(`/wayfinding/nodes/${nodeId}/coordinates`, data);
      return response.data?.data || response.data;
    } catch (error) {
      this.#handleError(error, 'Failed to update node coordinates');
    }
  }

  // ==========================================
  // Helper Methods
  // ==========================================

  /**
   * Handle API errors
   * @private
   * @param {Error} error - Error object
   * @param {string} defaultMessage - Default error message
   * @throws {Error} Enhanced error object
   */
  static #handleError(error, defaultMessage) {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || defaultMessage;
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.statusCode = error.response?.status;
    throw enhancedError;
  }
}

export default IndoorMappingService;
